/*
 * Profitape Quantity Rules enforcement.
 *
 * Source of truth: product metafields custom.mindestabnahmemenge / custom.mengenerhohung.
 * Registry: window.QuantityRules = { [productId]: { min, step } }
 *   - populated by inline <script> blocks rendered via snippets/quantity-rules-register.liquid
 *   - fetched on demand via /products/<handle>.js for items not pre-registered.
 *
 * Enforcement layers:
 *   1. DOM: every quantity input carries data-qty-min and data-qty-step; the plus/minus
 *      custom element clamps to these.
 *   2. fetch(): /cart/add.js, /cart/change.js, /cart/update.js are patched to clamp
 *      any quantity before the request leaves the browser.
 *   3. Reconciliation: after DOMContentLoaded and after every cart.js response we
 *      scan the cart and post a correction for line items that violate rules.
 *
 * Caveat: client-side only. A determined user with DevTools can still bypass this
 * (see README / inline comments). For the B2B shop target this is acceptable.
 */
(function () {
  'use strict';

  window.QuantityRules = window.QuantityRules || {};
  window.QuantityRulesCart = window.QuantityRulesCart || {}; // line_id -> { min, step, productId }

  var DEFAULT_MIN = 1;
  var DEFAULT_STEP = 1;

  function readRules(productId) {
    if (!productId) return null;
    var r = window.QuantityRules[productId];
    if (!r) return null;
    var min = parseInt(r.min, 10);
    var step = parseInt(r.step, 10);
    return {
      min: isFinite(min) && min > 0 ? min : DEFAULT_MIN,
      step: isFinite(step) && step > 0 ? step : DEFAULT_STEP,
    };
  }

  function clamp(qty, rules) {
    var q = parseInt(qty, 10);
    if (q === 0) return 0;
    if (!isFinite(q) || q < 0) return rules ? rules.min : DEFAULT_MIN;
    if (!rules) return q;
    if (q < rules.min) return rules.min;
    var delta = q - rules.min;
    var rem = delta % rules.step;
    if (rem === 0) return q;
    return q - rem;
  }

  window.QuantityRulesClamp = clamp;
  window.QuantityRulesRead = readRules;

  // ---------- Product registry loader for variants not pre-registered ----------
  var productLoaderCache = {};
  function loadProductById(productId, handle) {
    if (!handle) return Promise.resolve(null);
    if (window.QuantityRules[productId]) return Promise.resolve(window.QuantityRules[productId]);
    if (productLoaderCache[productId]) return productLoaderCache[productId];
    productLoaderCache[productId] = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return null;
        // The .js endpoint does not expose metafields. Fallback: if we already
        // have something in the registry keep it; otherwise register defaults.
        if (!window.QuantityRules[productId]) {
          window.QuantityRules[productId] = { min: DEFAULT_MIN, step: DEFAULT_STEP };
        }
        return window.QuantityRules[productId];
      })
      .catch(function () { return null; });
    return productLoaderCache[productId];
  }

  // ---------- Fetch interception ----------
  var CART_MUTATION_URLS = ['/cart/add.js', '/cart/change.js', '/cart/update.js', '/cart/add', '/cart/change', '/cart/update'];

  function isCartMutationUrl(url) {
    if (!url) return false;
    var u = String(url).split('?')[0];
    for (var i = 0; i < CART_MUTATION_URLS.length; i++) {
      if (u.endsWith(CART_MUTATION_URLS[i])) return true;
    }
    return false;
  }

  function variantIdToRules(variantId) {
    if (variantId == null) return null;
    var key = String(variantId);
    // Primary lookup: variant-id -> product-id registry.
    var pid = (window.QuantityRulesByVariant || {})[key] || (window.QuantityRulesByVariant || {})[parseInt(key, 10)];
    if (pid) {
      var r = readRules(pid);
      if (r) return r;
    }
    // Secondary: DOM attribute lookup for elements rendered without the JS map.
    var row = document.querySelector('[data-qty-variant-id="' + key + '"][data-qty-product-id]');
    if (row) {
      var pid2 = row.getAttribute('data-qty-product-id');
      var r2 = readRules(pid2);
      if (r2) return r2;
    }
    return null;
  }

  function lineKeyToRules(key) {
    // Cart line key looks like "12345:abcdef..." - item_count registry keyed by key.
    var entry = window.QuantityRulesCart[key];
    if (!entry) return null;
    return readRules(entry.productId);
  }

  function clampJsonBody(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    // /cart/add.js: { id: variantId, quantity: n } or { items: [{id, quantity}] }
    // /cart/change.js: { line|id|key, quantity }
    // /cart/update.js: { updates: { variantId|key: quantity } }
    if (obj.items && Array.isArray(obj.items)) {
      obj.items = obj.items.map(function (it) {
        var rules = variantIdToRules(it.id) || variantIdToRules(String(it.id));
        if (rules && it.quantity != null) it.quantity = clamp(it.quantity, rules);
        return it;
      });
    }
    if (obj.id != null && obj.quantity != null) {
      var rules = variantIdToRules(obj.id) || variantIdToRules(String(obj.id));
      if (rules) obj.quantity = clamp(obj.quantity, rules);
    }
    if (obj.key && obj.quantity != null) {
      var r = lineKeyToRules(obj.key);
      if (r) obj.quantity = clamp(obj.quantity, r);
    }
    if (obj.line != null && obj.quantity != null) {
      // line is 1-based index; map to key via window.QuantityRulesCartByLine
      var idx = parseInt(obj.line, 10);
      var lineEntry = (window.QuantityRulesCartByLine || [])[idx - 1];
      if (lineEntry) {
        var lr = readRules(lineEntry.productId);
        if (lr) obj.quantity = clamp(obj.quantity, lr);
      }
    }
    if (obj.updates && typeof obj.updates === 'object') {
      Object.keys(obj.updates).forEach(function (k) {
        var rules = variantIdToRules(k) || lineKeyToRules(k);
        if (rules) obj.updates[k] = clamp(obj.updates[k], rules);
      });
    }
    return obj;
  }

  function tryParseJson(str) {
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  function clampBody(body) {
    if (!body) return body;
    if (typeof body === 'string') {
      var parsed = tryParseJson(body);
      if (parsed) return JSON.stringify(clampJsonBody(parsed));
      // URL-encoded form body
      try {
        var params = new URLSearchParams(body);
        clampUrlSearchParams(params);
        return params.toString();
      } catch (e) { return body; }
    }
    if (body instanceof FormData) { clampFormData(body); return body; }
    if (body instanceof URLSearchParams) { clampUrlSearchParams(body); return body; }
    return body;
  }

  function clampFormData(fd) {
    var id = fd.get('id');
    var qty = fd.get('quantity');
    if (id && qty != null) {
      var rules = variantIdToRules(id);
      if (rules) fd.set('quantity', String(clamp(qty, rules)));
    }
    // updates[key]
    var keysToUpdate = [];
    fd.forEach(function (v, k) { if (k.indexOf('updates[') === 0) keysToUpdate.push(k); });
    keysToUpdate.forEach(function (k) {
      var key = k.slice(8, -1);
      var rules = variantIdToRules(key) || lineKeyToRules(key);
      if (rules) fd.set(k, String(clamp(fd.get(k), rules)));
    });
  }

  function clampUrlSearchParams(sp) {
    var id = sp.get('id');
    var qty = sp.get('quantity');
    if (id && qty != null) {
      var rules = variantIdToRules(id);
      if (rules) sp.set('quantity', String(clamp(qty, rules)));
    }
  }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (isCartMutationUrl(url)) {
        init = init || {};
        if (init.body != null) init.body = clampBody(init.body);
      }
    } catch (e) { /* never block the request */ }
    return origFetch.call(this, input, init);
  };

  // XHR patch for legacy callers
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__qrMutation = isCartMutationUrl(url);
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (this.__qrMutation && body != null) {
      try { body = clampBody(body); } catch (e) {}
    }
    return origSend.call(this, body);
  };

  // ---------- Reconciliation ----------
  var reconciling = false;
  function reconcileCart() {
    if (reconciling) return Promise.resolve();
    reconciling = true;
    return origFetch.call(window, '/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        var updates = {};
        var changed = false;
        cart.items.forEach(function (item) {
          var rules = readRules(item.product_id);
          // populate the line registry for subsequent mutations
          window.QuantityRulesCart[item.key] = { productId: item.product_id };
          window.QuantityRulesCartByLine = cart.items.map(function (it) {
            return { productId: it.product_id, key: it.key };
          });
          if (!rules) return;
          var c = clamp(item.quantity, rules);
          if (c !== item.quantity) { updates[item.key] = c; changed = true; }
        });
        if (!changed) return null;
        return origFetch.call(window, '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates }),
        }).then(function (r) { return r.ok ? r.json() : null; });
      })
      .catch(function () { return null; })
      .then(function (corrected) {
        reconciling = false;
        if (corrected) {
          // Nudge Dawn's cart sections to re-render.
          if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'quantity-rules', cartData: corrected });
          }
          document.dispatchEvent(new CustomEvent('quantity-rules:corrected', { detail: corrected }));
        }
      });
  }

  window.QuantityRulesReconcile = reconcileCart;

  // ---------- DOM-scan fallback ----------
  // Inline <script> tags injected via innerHTML/DOMParser do not execute, so
  // cart re-renders would lose their per-product registrations. This scanner
  // rebuilds the registry from data-qty-* attributes present on every rendered
  // quantity input + line item wrapper.
  function scanDomForRules() {
    document.querySelectorAll('[data-qty-product-id][data-qty-min][data-qty-step]').forEach(function (el) {
      var pid = el.getAttribute('data-qty-product-id');
      if (!pid) return;
      var min = parseInt(el.getAttribute('data-qty-min'), 10);
      var step = parseInt(el.getAttribute('data-qty-step'), 10);
      if (!isFinite(min) || min < 1) min = DEFAULT_MIN;
      if (!isFinite(step) || step < 1) step = DEFAULT_STEP;
      window.QuantityRules[pid] = { min: min, step: step };
      var vid = el.getAttribute('data-qty-variant-id') || el.getAttribute('data-quantity-variant-id');
      if (vid) {
        (window.QuantityRulesByVariant = window.QuantityRulesByVariant || {})[vid] = pid;
      }
    });
  }

  window.QuantityRulesScanDom = scanDomForRules;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanDomForRules);
  } else {
    scanDomForRules();
  }

  // Observe body for added subtrees (cart re-renders) and rescan.
  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(function (muts) {
      var relevant = false;
      for (var i = 0; i < muts.length; i++) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n && n.nodeType === 1 && (n.matches && (n.matches('[data-qty-product-id]') || n.querySelector && n.querySelector('[data-qty-product-id]')))) {
            relevant = true;
            break;
          }
        }
        if (relevant) break;
      }
      if (relevant) scanDomForRules();
    });
    if (document.body) mo.observe(document.body, { childList: true, subtree: true });
    else document.addEventListener('DOMContentLoaded', function () { mo.observe(document.body, { childList: true, subtree: true }); });
  }

  // ---------- Plus/Minus clamp overlay ----------
  // Intercepts Dawn's QuantityInput plus/minus by listening on document and
  // correcting the input value after the component already wrote it.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.quantity__button');
    if (!btn) return;
    var qtyInput = btn.closest('quantity-input, .quantity, form, body');
    if (!qtyInput) return;
    var input = qtyInput.querySelector('input.quantity__input, input[name="quantity"], input[type="number"]');
    if (!input) return;
    // Wait until Dawn's handler finishes (it calls stepUp/stepDown synchronously).
    requestAnimationFrame(function () {
      var min = parseInt(input.getAttribute('data-qty-min') || input.min, 10);
      var step = parseInt(input.getAttribute('data-qty-step') || input.step, 10);
      if (!isFinite(min) || min < 1) min = 1;
      if (!isFinite(step) || step < 1) step = 1;
      var clamped = clamp(input.value, { min: min, step: step });
      if (String(clamped) !== String(input.value)) {
        input.value = clamped;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }, true);

  // Clamp on blur/change of quantity fields.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('input.quantity__input, input[name="quantity"], input[type="number"][data-qty-min]')) return;
    var min = parseInt(input.getAttribute('data-qty-min') || input.min, 10);
    var step = parseInt(input.getAttribute('data-qty-step') || input.step, 10);
    if (!isFinite(min) || min < 1) min = 1;
    if (!isFinite(step) || step < 1) step = 1;
    var clamped = clamp(input.value, { min: min, step: step });
    if (String(clamped) !== String(input.value)) {
      input.value = clamped;
    }
  }, true);

  // ---------- Initial reconciliation + subscription ----------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileCart);
  } else {
    reconcileCart();
  }

  // Re-run after every cart re-render
  document.addEventListener('quantity-rules:trigger-reconcile', reconcileCart);
})();
